import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { setUnauthorizedHandler } from '@/shared/api/http-client';
import { tokenManager } from '@/shared/api/token-manager';

import { fetchMe } from '../api/auth.api';

import { AuthContext } from './auth-context';

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('checking');

  const clearSession = useCallback(() => {
    tokenManager.clear();
    setUser(null);
    setStatus('anonymous');
    queryClient.removeQueries({
      predicate: (query) => query.meta?.requiresAuth === true,
    });
  }, [queryClient]);

  const acceptSession = useCallback(({ user: nextUser, token, remember = false }) => {
    tokenManager.set(token, { remember });
    setUser(nextUser);
    setStatus('authenticated');
  }, []);

  useEffect(() => {
    let active = true;

    // A 401 on any authenticated request drops the session (no refresh flow).
    setUnauthorizedHandler(() => active && clearSession());

    // Restore a persisted session by validating the stored token against /me.
    if (!tokenManager.get()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount session restore; no stored token resolves straight to anonymous
      setStatus('anonymous');
    } else {
      fetchMe()
        .then((me) => {
          if (!active) return;
          setUser(me);
          setStatus('authenticated');
        })
        .catch(() => active && clearSession());
    }

    return () => {
      active = false;
      setUnauthorizedHandler(undefined);
    };
  }, [clearSession]);

  const value = useMemo(
    () => ({ user, status, acceptSession, clearSession, setUser }),
    [acceptSession, clearSession, status, user],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
